import { useEffect, useState } from "react";
import QuestionBox from "../../ui/QuestionBox/QuestionBox";
import RadioButton from "../../ui/RadioButton/RadioButton";
import classes from "./Infrastructure.module.css";
import QuestionCard from "../../ui/QuestionCard/QuestionCard";
import CommonFooter from "../../CommonFooter/CommonFooter";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { env } from "../../../constants/env";
import { Bounce, toast } from "react-toastify";
import { useParams } from "react-router-dom";
import CommonButton from "../../ui/CommonButton/CommonButton";

import { apiRequest } from "../../../utils/apiClient";
import { db } from "../../../utils/offlineDB";
import { isFormEmpty } from "../../../utils/functions";

const initialState = {
  isDedicatedLabArea: "",
  infrastructureChallenges: "",
  isVentilated: "",
  ventilationComment: "",
  infrastructureChallenges2: ["", ""],
  waterSourceChallenges: "",
  energySources: ["", ""],
  energySourceChallenges: "",
  hasStaffToilets: "",
  staffToiletsComment: "",
  hasPatientToilets: "",
  patientToiletsComment: "",
  hasAircons: "",
  airconComment: "",
  officeSpaceAvailability: {
    labIncharge: "",
    safetyOfficer: "",
    qualityOfficer: "",
    logisticsOfficer: "",
  },
  officeSpaceComment: "",
  storageFacilities: {
    reagentStorageComment: "",
    hasShelves: "",
    hasRefrigerators: "",
    refrigeratorsComment: "",
    hasThermometers: "",
    thermometersComment: "",
  },
};

const Infrastructure = () => {
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState(["", ""]);
  const { user } = useAuth();

  const { labId: paramLabId } = useParams();
  const labId = paramLabId || user?.labId;

  console.log("LAB ID:", labId);

  const userRole = user?.role;

  const [formData, setFormData] = useState(initialState);
  const [ownerLabId, setOwnerLabId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const isReadOnly = userRole !== "LABORATORY" || !editMode;

  const FORM_TYPE = "infrastructure";

  useEffect(() => {
    if (labId) {
      fetchQuestionnaire();
    }
  }, [labId]);

  const loadLocal = async () => {
    return await db.questionnaires.get({ labId, formType: FORM_TYPE });
  };

  // Update the saveLocal function in Infrastructure.js
  const saveLocal = async (data, isOnline = false) => {
    // IMPORTANT: The labId and formType must be in the object for Dexie indexing
    const record = {
      labId, // This must be included
      formType: FORM_TYPE, // This must be included
      ...data,
      timestamp: Date.now(),
      offline: !isOnline && !navigator.onLine, // Only mark as offline if not synced AND user is offline
    };

    // Use put (upsert) instead of add
    await db.questionnaires.put(record);
    console.log("Saved locally:", {
      labId,
      formType: FORM_TYPE,
      ...record,
    });
  };

  // useEffect(() => {
  //   if (labId) {
  //     fetchQuestionnaire();
  //   }
  // }, [labId]);

  const fetchQuestionnaire = async () => {
    setIsFetching(true);

    const cached = await loadLocal();

    // --- OFFLINE MODE ---
    if (!navigator.onLine) {
      if (cached) {
        // Check if cached data is newer than what we have in state
        const shouldUseCached =
          !formData.timestamp || cached.timestamp > formData.timestamp;

        if (shouldUseCached) {
          const a = cached.answers || {};
          const isEmpty = isFormEmpty(a);
          setFormData({ ...initialState, ...a, timestamp: cached.timestamp });
          setEditMode(isEmpty);
          setOwnerLabId(cached.labId || labId);
          console.log(
            "Loaded from offline cache (timestamp:",
            cached.timestamp,
            ")"
          );
        }
      } else {
        toast.warn("Offline: No local data found.");
        setFormData(initialState);
        setEditMode(true);
      }
      setIsFetching(false);
      return;
    }

    // ONLINE MODE
    try {
      const res = await axios.get(
        `${env.kataApiUrl}/api/questionnaire/${labId}/${FORM_TYPE}`
      );

      const serverData = res.data;
      const serverAnswers = serverData?.answers || {};

      // Check for conflicts
      if (cached?.offline) {
        // Simple conflict resolution: Use newer timestamp
        const serverTime = new Date(
          serverData.updatedAt || serverData.timestamp || 0
        );
        const localTime = new Date(cached.timestamp || 0);

        if (localTime > serverTime) {
          // Local is newer - show conflict warning
          toast.warn("Offline edits found. Submitting to server...");

          // Auto-resolve by submitting local version
          const payload = {
            labId,
            formType: FORM_TYPE,
            answers: cached.answers || {},
            timestamp: cached.timestamp,
          };

          try {
            const submitRes = await axios.post(
              `${env.kataApiUrl}/api/questionnaire/${FORM_TYPE}`,
              payload
            );

            console.log("Conflict resolved by submitting local version", submitRes.data);

            // Update with server response
            setFormData({
              ...initialState,
              ...submitRes.data.data.answers,
            });
            await saveLocal(submitRes.data.data, true);
          } catch (submitErr) {
            // If submission fails, show both versions
            toast.error("Conflict detected. Please review changes.");
            setFormData({
              ...initialState,
              ...cached.answers,
              _conflict: true,
              _serverVersion: serverAnswers,
            });
          }
        } else {
          // Server is newer - use server data
          setFormData({
            ...initialState,
            ...serverAnswers,
          });
          await saveLocal(serverData);
        }
      } else {
        // No conflicts, use server data
        setFormData({
          ...initialState,
          ...serverAnswers,
        });
        await saveLocal(serverData);
      }

      setOwnerLabId(serverData.labId || labId);
      setEditMode(isFormEmpty(serverAnswers));
    } catch (error) {
      // Server error - fallback to cached data
      if (cached) {
        toast.warn("Server unreachable. Loaded offline version.");
        const a = cached.answers || {};
        const isEmpty = isFormEmpty(a);
        setFormData({
          ...initialState,
          ...a,
          timestamp: cached.timestamp,
        });
        setEditMode(isEmpty);
        setOwnerLabId(cached.labId || labId);
      } else {
        toast.error("Failed to load questionnaire (no offline backup)");
        setFormData(initialState);
        setEditMode(true);
      }
    } finally {
      setIsFetching(false);
    }
  };

  const handleChange = (field, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedChange = (section, key, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  const handleDone = async () => {
    setLoading(true);

    const payload = {
      labId,
      formType: FORM_TYPE,
      answers: formData,
      timestamp: Date.now(), // Add timestamp
    };

    try {
      if (navigator.onLine) {
        // Try to submit online first
        const res = await axios.post(
          `${env.kataApiUrl}/api/questionnaire/${FORM_TYPE}`,
          payload
        );

        toast.success("Submitted successfully!");

        // Save with offline: false since it's synced
        await saveLocal(
          {
            ...res.data,
            answers: formData,
          },
          true
        ); // true means this was an online submission

        setEditMode(false);
      } else {
        // Save offline
        await saveLocal(payload);
        toast.info("Saved offline. Will sync when online.");
        setEditMode(false);
      }
    } catch (error) {
      if (navigator.onLine) {
        // Online submission failed, save offline
        toast.error("Submission failed. Saved offline.");
        await saveLocal(payload);
      } else {
        // Already offline, just save
        toast.info("Saved offline.");
        await saveLocal(payload);
      }
    } finally {
      setLoading(false);
      fetchQuestionnaire(); // Refresh data
    }
  };

  const questionSets = [
    {
      title: "Laboratory infrastructure",
      subtitle: "A. Laboratory testing areas",
      content: (
        <div className="d-flex flex-column gap-4">
          <QuestionBox
            number={1}
            question="Is there an area dedicated specifically to the Laboratory?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-4">
              <RadioButton
                label="Yes"
                name="isDedicatedLabArea"
                checked={formData.isDedicatedLabArea === "Yes"}
                onChange={() => handleChange("isDedicatedLabArea", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="isDedicatedLabArea"
                checked={formData.isDedicatedLabArea === "No"}
                onChange={() => handleChange("isDedicatedLabArea", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={2}
            question="Is there enough Ventilation?"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.ventilationComment}
            onChange={(e) => handleChange("ventilationComment", e.target.value)}
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="isVentilated"
                checked={formData.isVentilated === "Yes"}
                onChange={() => handleChange("isVentilated", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="isVentilated"
                checked={formData.isVentilated === "No"}
                onChange={() => handleChange("isVentilated", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={3}
            question="Challenges with infrastructure"
            singleInput
            answers={formData.infrastructureChallenges2}
            onAnswersChange={(newAnswers) =>
              handleChange("infrastructureChallenges2", newAnswers)
            }
            disabled={isReadOnly}
          />
          <QuestionBox
            number={4}
            question="Challenges with water source"
            multilineInput={true}
            value={formData.waterSourceChallenges}
            onChange={(e) =>
              handleChange("waterSourceChallenges", e.target.value)
            }
            disabled={isReadOnly}
          />
          <QuestionBox
            number={5}
            question="List the energy source is the Laboratory using?"
            singleInput={true}
            answers={formData.energySources}
            onAnswersChange={(newAnswers) =>
              handleChange("energySources", newAnswers)
            }
            disabled={isReadOnly}
          />
          <QuestionBox
            number={6}
            question="Challenges with energy source"
            multilineInput={true}
            value={formData.energySourceChallenges}
            onChange={(e) =>
              handleChange("energySourceChallenges", e.target.value)
            }
            disabled={isReadOnly}
          />
          <QuestionBox
            number={7}
            question="Availability of Staff Toilets"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.staffToiletsComment}
            onChange={(e) =>
              handleChange("staffToiletsComment", e.target.value)
            }
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasStaffToilets"
                checked={formData.hasStaffToilets === "Yes"}
                onChange={() => handleChange("hasStaffToilets", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasStaffToilets"
                checked={formData.hasStaffToilets === "No"}
                onChange={() => handleChange("hasStaffToilets", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={8}
            question="Availability of Patient Toilets"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.patientToiletsComment}
            onChange={(e) =>
              handleChange("patientToiletsComment", e.target.value)
            }
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasPatientToilets"
                checked={formData.hasPatientToilets === "Yes"}
                onChange={() => handleChange("hasPatientToilets", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasPatientToilets"
                checked={formData.hasPatientToilets === "No"}
                onChange={() => handleChange("hasPatientToilets", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={9}
            question="Availability of Aircons"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.airconComment}
            onChange={(e) => handleChange("airconComment", e.target.value)}
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasAircons"
                checked={formData.hasAircons === "Yes"}
                onChange={() => handleChange("hasAircons", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasAircons"
                checked={formData.hasAircons === "No"}
                onChange={() => handleChange("hasAircons", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={10}
            question="Availability of Office Space for"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.officeSpaceComment}
            onChange={(e) => handleChange("officeSpaceComment", e.target.value)}
            disabled={isReadOnly}
          >
            <div className="d-flex flex-column gap-2 w-75 mb-4">
              {[
                "labIncharge",
                "safetyOfficer",
                "qualityOfficer",
                "logisticsOfficer",
              ].map((role) => (
                <div className="d-flex gap-2" key={role}>
                  <QuestionCard
                    question={
                      <span className={classes.questionText}>
                        {role.replace(/([A-Z])/g, " $1")}
                      </span>
                    }
                  />
                  <RadioButton
                    label="Yes"
                    name={role}
                    checked={formData.officeSpaceAvailability[role] === "Yes"}
                    onChange={() =>
                      handleNestedChange("officeSpaceAvailability", role, "Yes")
                    }
                    disabled={isReadOnly}
                  />
                  <RadioButton
                    label="No"
                    name={role}
                    checked={formData.officeSpaceAvailability[role] === "No"}
                    onChange={() =>
                      handleNestedChange("officeSpaceAvailability", role, "No")
                    }
                    disabled={isReadOnly}
                  />
                </div>
              ))}
            </div>
          </QuestionBox>
        </div>
      ),
    },
    {
      title: "Laboratory infrastructure",
      subtitle: "B. Storage facilities",
      content: (
        <div className="d-flex flex-column gap-4">
          <QuestionBox
            number={1}
            question="Is there Area dedicated for reagent storage?"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.storageFacilities.reagentStorageComment}
            onChange={(e) =>
              handleNestedChange(
                "storageFacilities",
                "reagentStorageComment",
                e.target.value
              )
            }
            disabled={isReadOnly}
          ></QuestionBox>
          <QuestionBox
            number={2}
            question="Availability of Shelves in the storage Area?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-4">
              <RadioButton
                label="Yes"
                name="hasShelves"
                checked={formData.storageFacilities.hasShelves === "Yes"}
                onChange={() =>
                  handleNestedChange("storageFacilities", "hasShelves", "Yes")
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasShelves"
                checked={formData.storageFacilities.hasShelves === "No"}
                onChange={() =>
                  handleNestedChange("storageFacilities", "hasShelves", "No")
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={3}
            question="Availability of Refrigerators in the store room?"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.storageFacilities.refrigeratorsComment}
            onChange={(e) =>
              handleNestedChange(
                "storageFacilities",
                "refrigeratorsComment",
                e.target.value
              )
            }
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasRefrigerators"
                checked={formData.storageFacilities.hasRefrigerators === "Yes"}
                onChange={() =>
                  handleNestedChange(
                    "storageFacilities",
                    "hasRefrigerators",
                    "Yes"
                  )
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasRefrigerators"
                checked={formData.storageFacilities.hasRefrigerators === "No"}
                onChange={() =>
                  handleNestedChange(
                    "storageFacilities",
                    "hasRefrigerators",
                    "No"
                  )
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={4}
            question="Availability of thermometers"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.storageFacilities.thermometersComment}
            onChange={(e) =>
              handleNestedChange(
                "storageFacilities",
                "thermometersComment",
                e.target.value
              )
            }
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasThermometers"
                checked={formData.storageFacilities.hasThermometers === "Yes"}
                onChange={() =>
                  handleNestedChange(
                    "storageFacilities",
                    "hasThermometers",
                    "Yes"
                  )
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasThermometers"
                checked={formData.storageFacilities.hasThermometers === "No"}
                onChange={() =>
                  handleNestedChange(
                    "storageFacilities",
                    "hasThermometers",
                    "No"
                  )
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
        </div>
      ),
    },
  ];

  const { subtitle, content } = questionSets[currentSet];

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div
      className={`${classes.wrapper} ${paramLabId ? classes.fromParams : ""} ${
        isReadOnly ? classes.readOnly : ""
      }`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <h2>Laboratory infrastructure</h2>
        {userRole === "LABORATORY" && !editMode && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditMode(true)}
            title="Edit"
          />
        )}
      </div>
      <h4>{subtitle}</h4>
      {!editMode && userRole !== "MINISTRY" ? (
        <div className={classes.infoText}>
          ✅ Your laboratory infrastructure has been submitted.
        </div>
      ) : (
        content
      )}

      {!isReadOnly && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => {
            setEditMode(false);
            fetchQuestionnaire();
            setCurrentSet(0);
          }}
          questionSets={questionSets}
          currentSet={currentSet}
          disabled={loading}
          doneText={loading ? "Submitting..." : "Submit"}
        />
      )}
    </div>
  );
};

export default Infrastructure;
